import React, { useEffect, useState } from "react";
import { Box, Typography, Button, IconButton, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import SelectField from "../../../../Components/Masters/SelectField";
import countries from "../../Roles and Permissions/Country.json";
import EditDialog from "../../../../Components/Common/EditDialog";
import { post, put } from "../../../../utils/api";

const INVESTMENT_TYPE_OPTIONS = [
  "Subsidiaries",
  "Associates",
  "Joint Ventures",
  "Minority stakes (<20%)",
  "Project-linked loans or bonds",
  "Equity or debt financing for specific projects",
];

const SECTOR_OPTIONS = [
  "Energy (renewable & non-renewable)",
  "Manufacturing & industrials",
  "Transportation & logistics",
  "Real estate & infrastructure",
  "Agriculture, forestry & land use",
  "Mining & metals",
  "Oil & gas",
  "Utilities (power, water, waste)",
  "Technology & data centers",
  "Retail & consumer goods",
  "Healthcare & pharmaceuticals",
  "Financial services & fintech",
  "Telecommunications",
  "Construction & materials",
];

function AddInvestment({
  formInputData,
  fetchData,
  id,
  element,
  setAddData,
  addData,
  setsnackOpen,
  addUrl,
  updateUrl,
  embedded = false,
  embeddedTitle = "",
}) {
  const [errors, setErrors] = useState({});

  const buildInitialState = () => ({
    investmentType: element?.investmentType || "",
    investeeProjectName: element?.investeeProjectName || "",
    sector: element?.sector || "",
    countryOfOperation: element?.countryOfOperation || "",
    equityHoldingPercent:
      element?.equityHoldingPercent !== undefined
        ? String(element.equityHoldingPercent)
        : "",
  });

  const [formData, setFormData] = useState(buildInitialState);
  const [editDialog, setEditDialog] = useState(false);
  const [formData2, setFormData2] = useState(buildInitialState);

  useEffect(() => {
    const next = buildInitialState();
    setFormData(next);
    setFormData2(next);
  }, [element, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "equityHoldingPercent" && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCountryChange = (event) => {
    const selectedCountry = event.target.value;
    setErrors((prev) => ({ ...prev, countryOfOperation: "" }));
    setFormData((prev) => ({
      ...prev,
      countryOfOperation:
        selectedCountry === "Tap to select" ? "" : selectedCountry,
    }));
  };

  const handleExitAgree = () => {
    setEditDialog(false);
    setAddData(false);
    setErrors({});
    const cleared = buildInitialState();
    setFormData(cleared);
    setFormData2(cleared);
  };

  const handleClose = () => {
    const unchanged = Object.keys(formData).every(
      (key) => formData[key] === formData2[key]
    );
    if (unchanged) handleExitAgree();
    else setEditDialog(true);
  };

  const validateForm = () => {
    const next = { ...errors };
    formInputData.forEach((input) => {
      const value = formData[input.fieldName];
      if (!value || String(value).trim() === "") {
        next[input.fieldName] = `${input.title} is required`;
      } else {
        delete next[input.fieldName];
      }
    });
    const eq = Number(formData.equityHoldingPercent);
    if (Number.isNaN(eq) || eq < 0 || eq > 100) {
      next.equityHoldingPercent = "Equity Holding (%) must be between 0 and 100";
    }
    setErrors(next);
    return !Object.keys(next).some((k) => next[k]);
  };

  const Save = async () => {
    if (!validateForm()) return;
    const payload = {
      investmentType: formData.investmentType,
      investeeProjectName: formData.investeeProjectName.trim(),
      sector: formData.sector,
      countryOfOperation: formData.countryOfOperation,
      equityHoldingPercent: Number(formData.equityHoldingPercent),
    };

    const request = id
      ? put(`${updateUrl}?Id=${id}`, payload)
      : post(addUrl, payload);

    await request
      .then((res) => {
        setsnackOpen({
          open: true,
          message: res.data.message,
          severity: "success",
        });
        fetchData();
        handleExitAgree();
      })
      .catch((err) => {
        const apiMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "";
        const isDuplicateNameError =
          typeof apiMessage === "string" &&
          /already exists|already used|duplicate/i.test(apiMessage);

        if (isDuplicateNameError) {
          setErrors((prev) => ({
            ...prev,
            investeeProjectName: "Name already used",
          }));
          return;
        }

        if (err.response?.data?.errors) {
          const errorMessages = err.response.data.errors
            .map((error) => error.msg)
            .join(", ");
          setsnackOpen({
            open: true,
            message: errorMessages,
            severity: "error",
          });
        } else {
          setsnackOpen({
            open: true,
            message: err?.response?.data?.message || "Something went wrong",
            severity: "error",
          });
        }
      });
  };

  const renderField = (el) => {
    if (el.fieldName === "investmentType") {
      return (
        <SelectField
          key={el.fieldName}
          title={el.title}
          name={el.fieldName}
          value={formData.investmentType}
          handleChange={handleChange}
          options={INVESTMENT_TYPE_OPTIONS}
          errors={errors[el.fieldName]}
        />
      );
    }

    if (el.fieldName === "sector") {
      return (
        <SelectField
          key={el.fieldName}
          title={el.title}
          name={el.fieldName}
          value={formData.sector}
          handleChange={handleChange}
          options={SECTOR_OPTIONS}
          errors={errors[el.fieldName]}
        />
      );
    }

    if (el.fieldName === "countryOfOperation") {
      return (
        <SelectField
          key={el.fieldName}
          title={el.title}
          name={el.fieldName}
          value={formData.countryOfOperation}
          handleChange={handleCountryChange}
          options={countries.countries}
          errors={errors[el.fieldName]}
        />
      );
    }

    return (
      <Box key={el.fieldName}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
          {el.title}
        </Typography>
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          name={el.fieldName}
          value={formData[el.fieldName] || ""}
          onChange={handleChange}
          placeholder={el.placeholder}
          disabled={Boolean(el.readOnly)}
          error={Boolean(errors[el.fieldName])}
          helperText={
            typeof errors[el.fieldName] === "string" ? errors[el.fieldName] : ""
          }
          inputProps={
            el.fieldName === "equityHoldingPercent"
              ? { inputMode: "decimal" }
              : undefined
          }
        />
      </Box>
    );
  };

  const drawerList = () => (
    <Box display="flex" gap={3} sx={{ width: "28rem", maxWidth: "100%" }}>
      {editDialog && (
        <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
      )}
      <Box
        sx={{
          backgroundColor: "#fff",
          height: "20px",
          padding: "13px",
          margin: "30px 0 0 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "80%",
          width: "5%",
        }}
      >
        <IconButton onClick={handleClose} sx={{ color: "black" }}>
          <CloseIcon sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
      <Box
        sx={{
          width: "90%",
          p: 3,
          backgroundColor: "#fff",
          height: "100vh",
          overflowY: "scroll",
        }}
      >
        <Typography
          variant="h6"
          sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
        >
          {id ? "Edit Investment" : "Add Investment"}
        </Typography>
        {formInputData.map((el) => renderField(el))}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0a3f73",
              textTransform: "none",
              marginTop: "10%",
            }}
            onClick={Save}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  );

  const embeddedList = () => (
    <Box
      sx={{
        width: "100%",
        p: 0,
        backgroundColor: "transparent",
      }}
    >
      {editDialog && (
        <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ textDecoration: "underline", fontWeight: "bold" }}
        >
          {embeddedTitle || (id ? "Edit Investment" : "Add Investment")}
        </Typography>
      </Box>
      {formInputData.map((el) => renderField(el))}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#0a3f73",
            textTransform: "none",
            mt: 1,
            mb: 6,
          }}
          onClick={Save}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );

  if (embedded) {
    return addData ? embeddedList() : null;
  }

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        height: "100vh",
        minHeight: "100vh",
        overflowY: "auto",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={addData}
      onClose={handleClose}
    >
      {drawerList()}
    </Drawer>
  );
}

export default AddInvestment;
